import {
	LayoutDashboard,
	NetworkIcon,
	UserCog,
	SortAsc,
	ListOrdered,
	RefreshCcw,
	Lightbulb,
	ScreenShare,
	Link,
	// Save,
	AudioLines,
	NewspaperIcon,
	MailCheckIcon,
	HelpCircle,
} from "lucide-react";
import {
	Sidebar,
	SidebarContent,
	SidebarFooter,
	SidebarGroup,
	SidebarGroupContent,
	SidebarHeader,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
} from "@/shared/components/ui/Sidebar";
import { CollapsibleMenu, CollapsibleSubmenuLink } from "./ui/Collapsible";
import { useNavigate, Path, useLocation } from "react-router-dom";
import { persistentStore, useAppSelector } from "@/redux/store";
import { useGetSsoLoginUrlQuery } from "@/redux/slices/creativeBuilderSlice";
import { useEffect, useMemo, useState } from "react";
import { defaultSessionUser, useLazyGetUserDataQuery } from "@/redux/slices/userDataSlice";
import { SessionUser } from "@/types/AuthUser";
import SpinnerLoader from "./SpinnerLoader";
import { AssetSideBar } from "./AssetSideBar";
import { resetOrderListingFilters, setPerPage, setStatusValue } from "@/redux/slices/filterSlice";
import { useDispatch } from "react-redux";
import {
	generateAgencyOrderListingPageUrl,
	getParseOrderSearchDefaultData,
	getPrefixBeforeSlash,
} from "@/shared/lib/utils";
import { format, subDays } from "date-fns";
import { useSupport } from "../hooks/useSupport";
import { Button } from "./ui/Button";

const { pathname } = location;

const isActiveLink = (shouldActive: () => void) =>
	shouldActive && typeof shouldActive === "function" && shouldActive() ? "bg-[#004F8D] text-white" : "";

const isMenuOpen = (location: Path, links: CollapsibleSubmenuLink[], isAdminManage = false): boolean => {
	if (isAdminManage && location.pathname.match(/\/admin\/manage\/agency\/\d+\/[profile|billing|user|whitelabel]/)) {
		return true;
	}
	return links.some(link => location.pathname.includes(link.url));
};

const networkManagementLinks: CollapsibleSubmenuLink[] = [
	{
		title: "oRTB Network List",
		url: "/admin/rtb/networks",
		linkClasses: isActiveLink(() => location.pathname.includes("/admin/rtb/networks")),
	},
	{
		title: "Campaign CSV Exporter",
		url: "/admin/campaign-csv/list",
		linkClasses: isActiveLink(() => location.pathname.includes("/admin/campaign-csv/list")),
	},
	{
		title: "CSV Spreadsheet Exporter",
		url: "/admin/campaign-csv/spreadsheet",
		linkClasses: isActiveLink(() => location.pathname.includes("/admin/campaign-csv/spreadsheet")),
	},
];

const trafficAndAudienceLinks: CollapsibleSubmenuLink[] = [
	{
		title: "Audience Lists",
		url: "/traffic-estimator",
		linkClasses: isActiveLink(
			() =>
				location.pathname.includes("/traffic-estimator") && !location.pathname.includes("/traffic-estimator/builder"),
		),
	},
	{
		title: "New Audience",
		url: "/traffic-estimator/builder",
		linkClasses: isActiveLink(() => location.pathname.includes("/traffic-estimator/builder")),
	},
];

const pdMarketingEmailLinks: CollapsibleSubmenuLink[] = [
	{
		title: "Manage Email Templates",
		url: "/admin/email-templates",
		linkClasses: isActiveLink(() => pathname.startsWith("/admin/email-templates")),
	},
	{
		title: "Email Marketing Campaigns",
		url: "/admin/pd-marketing/list",
		linkClasses: isActiveLink(
			() => pathname.startsWith("/admin/pd-marketing/list") || pathname.startsWith("/admin/pd-marketing/form"),
		),
	},
	{
		title: "Manage Email Sequences",
		url: "/admin/pd-marketing/sequence/list",
		linkClasses: isActiveLink(() => pathname.startsWith("/admin/pd-marketing/sequence")),
	},
	{
		title: "Sequence Campaigns",
		url: "/admin/pd-marketing/send-sequence/list",
		linkClasses: isActiveLink(() => pathname.startsWith("/admin/pd-marketing/send-sequence")),
	},
	{
		title: "Email Profiles",
		url: "/admin/pd-marketing/profile/list",
		linkClasses: isActiveLink(() => pathname.startsWith("/admin/pd-marketing/profile")),
	},
	{
		title: "Lists",
		url: "/admin/pd-marketing-list/list",
		linkClasses: isActiveLink(() => pathname.startsWith("/admin/pd-marketing-list")),
	},
];
const accountManagementLinks: CollapsibleSubmenuLink[] = [
	{
		title: "Agency List",
		url: "/admin/manage/agency/list/ACTIVE/company_name/ASC",
		linkClasses: isActiveLink(
			() =>
				location.pathname.includes("/admin/manage/agency/list") ||
				location.pathname.match(/\/admin\/manage\/agency\/\d+\/(.*)/),
		),
	},
	{
		title: "Agency Users List",
		url: "/admin/manage/user/list/first_name/ASC",
		linkClasses: isActiveLink(() => location.pathname.includes("/admin/manage/user/list")),
	},
];

const doohScreenResearchLinks: CollapsibleSubmenuLink[] = [
	{
		title: "Screen Lookup",
		url: "/dooh/screen-lookup",
		linkClasses: isActiveLink(() => location.pathname.includes("/dooh/screen-lookup")),
	},
	{
		title: "DOOH Screen Lists",
		url: "/dooh/audience/list",
		linkClasses: isActiveLink(() => location.pathname.includes("/dooh/audience/list")),
	},
];

export const AppSidebar = ({ isStorageSideBar = false }) => {
	const location = useLocation();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const basePath = location.pathname.split("/").slice(0, 3).join("/");

	const whitelabel = useAppSelector(state => state.whitelabelConfigs);
	const [fetchSessionUser, { isLoading }] = useLazyGetUserDataQuery(undefined);
	const [me, setMe] = useState<SessionUser>(defaultSessionUser);
	const { data: ssoLoginUrl } = useGetSsoLoginUrlQuery(null);

	const orderSearchDefault = useAppSelector(state =>
		getPrefixBeforeSlash(state.sessionUser.order_search_default ?? "GLOBAL"),
	);

	const { triggerSupportOpen } = useSupport();

	const staticMenuItems = useMemo(
		() => [
			{
				title: "Campaign Management",
				url: "/agency-order/order-listing",
				icon: ListOrdered,
				isVisible: (user: SessionUser) => user.is_order_create === "Y",
			},
			{
				title: "Audio A.I. Creative",
				url: "/ai/audio/list",
				icon: AudioLines,
				isVisible: (user: SessionUser) => user.is_admin === "Y",
			},
			{
				title: "Renewal Reporting",
				url: "/agency-order/renewal_reporting",
				icon: RefreshCcw,
				isVisible: (user: SessionUser) => user.is_renewal_reporting === "Y",
			},
			{
				title: "Financial Reporting",
				url: "/billing/report",
				icon: NewspaperIcon,
				isVisible: (user: SessionUser) => user.is_admin === "Y",
			},
		],
		[],
	);

	useEffect(() => {
		fetchSessionUser(undefined).then(response => {
			setMe(response.data?.data.user as SessionUser);
		});
	}, [fetchSessionUser, setMe]);

	const creativeBuilderMenu = useMemo(
		() => ({
			title: "Creative Builder",
			url: ssoLoginUrl?.data.url,
			icon: Lightbulb,
		}),
		[ssoLoginUrl],
	);

	return (
		<Sidebar className="h-full">
			<SidebarHeader>
				<img src={whitelabel.logo_url} alt={`${whitelabel.company_name} Logo`} className="mr-auto mb-5 w-[70%]" />
			</SidebarHeader>
			<SidebarContent>
				{!me || isLoading ?
					<SidebarGroup>
						<SpinnerLoader />
					</SidebarGroup>
				:	<SidebarGroup>
						{isStorageSideBar ?
							<>
								<AssetSideBar />
							</>
						:	<SidebarGroupContent>
								{/* Dashboard Button */}
								{me.is_pd_email_marketing === "N" && (
									<SidebarMenuButton asChild hidden={me?.is_show_dashboard_on_login !== "Y"}>
										<a className={`${isActiveLink(() => location.pathname === "/dashboard")} mb-1`} href={"/dashboard"}>
											<LayoutDashboard />
											<span>User Dashboard</span>
										</a>
									</SidebarMenuButton>
								)}

								{/* Network Management Collapsible Menu */}
								{me.is_admin === "Y" && me.is_network_admin === "Y" && me.is_pd_email_marketing === "N" ?
									<CollapsibleMenu
										openByDefault={isMenuOpen(location, networkManagementLinks)}
										title="Network Management"
										icon={NetworkIcon}
										links={networkManagementLinks}
									/>
								:	""}

								{/* User Management Collapsible Menu */}
								{me.is_agency_admin === "Y" && me.is_pd_email_marketing === "N" && (
									<SidebarMenuButton asChild>
										<a
											className={`${isActiveLink(() => location.pathname.includes("/manage/user/list"))} mb-1`}
											href={"/manage/user/list/first_name/ASC"}
										>
											<LayoutDashboard />
											<span>User Management</span>
										</a>
									</SidebarMenuButton>
								)}

								{/* Account Management Collapsible Menu */}
								{(
									(me.is_admin === "Y" || me.is_staff === "Y") &&
									me.is_demouser === "N" &&
									me.is_pd_email_marketing === "N"
								) ?
									<CollapsibleMenu
										openByDefault={isMenuOpen(location, accountManagementLinks, true)}
										title="Account Management"
										icon={UserCog}
										links={accountManagementLinks}
									/>
								:	""}

								{/* Traffic & Audience Collapsible Menu */}
								{me.is_traffic_estimator === "Y" && me.is_pd_email_marketing === "N" ?
									<CollapsibleMenu
										openByDefault={isMenuOpen(location, trafficAndAudienceLinks)}
										title="Audience Builder"
										icon={SortAsc}
										links={trafficAndAudienceLinks}
									/>
								:	""}

								{/* PD Marketing - Email Collapsible Menu */}
								{me.is_admin === "Y" || me.is_pd_email_marketing === "Y" ?
									<CollapsibleMenu
										openByDefault={isMenuOpen(location, pdMarketingEmailLinks)}
										title="PD Marketing - Email"
										icon={MailCheckIcon}
										links={pdMarketingEmailLinks}
									/>
								:	""}

								{/* Static Sidebar Menu */}
								{me.is_pd_email_marketing === "N" && (
									<SidebarMenu>
										{staticMenuItems.map(item => {
											if (!item.isVisible(me)) return null;

											const isActive = location.pathname.includes(item.url) || basePath === item.url;

											const handleClick = async (
												e: React.MouseEvent<HTMLAnchorElement>,
												item: { url: string; title: string },
											) => {
												e.preventDefault();

												if (item.url === "/agency-order/order-listing") {
													try {
														const routeConfig = getParseOrderSearchDefaultData(orderSearchDefault);
														const defaultParams = {
															startDate: format(subDays(new Date(), 63), "yyyy-MM-dd"),
															endDate: format(new Date(), "yyyy-MM-dd"),
															status: orderSearchDefault,
															agencyFilter: "-",
															perPage: me.is_jason_or_mike === "Y" ? 200 : 100,
															page: 1,
															sortColumn: "o.create_time",
															sortDir: "desc",
														};

														dispatch(resetOrderListingFilters());
														dispatch(setStatusValue(defaultParams.status));
														dispatch(setPerPage(defaultParams.perPage));
														// Persist the reset before full-page navigation so stale filters cannot rehydrate.
														await persistentStore.flush();

														window.location.href = generateAgencyOrderListingPageUrl(routeConfig.path, defaultParams);
													} catch (error) {
														console.error("Dispatch failed, navigation cancelled", error);
													}
													return;
												}

												navigate(item.url);
											};

											return (
												<SidebarMenuItem key={item.title}>
													<SidebarMenuButton asChild>
														<a
															href={item.url}
															onClick={e => handleClick(e, item)}
															className={`${isActiveLink(() => isActive)}`}
														>
															<item.icon />
															<span>{item.title}</span>
														</a>
													</SidebarMenuButton>
												</SidebarMenuItem>
											);
										})}

										{me.is_creativebuilder === "Y" || me.is_admin === "Y" ?
											<SidebarMenuItem key={creativeBuilderMenu.title}>
												<SidebarMenuButton asChild>
													<a
														href={creativeBuilderMenu.url}
														className={`${isActiveLink(() => location.pathname === creativeBuilderMenu.url)}`}
														target="_blank"
													>
														<creativeBuilderMenu.icon />
														<span>{creativeBuilderMenu.title}</span>
													</a>
												</SidebarMenuButton>
											</SidebarMenuItem>
										:	""}
									</SidebarMenu>
								)}

								{/* DOOH */}
								{(me.is_dooh_screen_query === "Y" || me.is_admin === "Y") && me.is_pd_email_marketing === "N" ?
									<div className="my-1">
										<CollapsibleMenu
											openByDefault={isMenuOpen(location, doohScreenResearchLinks)}
											title="DOOH Screen Research"
											icon={ScreenShare}
											links={doohScreenResearchLinks}
										/>
									</div>
								:	""}

								{me.is_pd_email_marketing === "N" && (
									<SidebarMenuButton asChild>
										<a
											className={`${isActiveLink(() => location.pathname === "/url-parameter-manager")} mb-1`}
											href={"/url-parameter-manager"}
										>
											<Link />
											<span>URL Parameter Manager</span>
										</a>
									</SidebarMenuButton>
								)}

								{/* <SidebarMenuButton asChild>
									<a
										className={`${isActiveLink(() => location.pathname === "/assets/setup")} mb-1`}
										href={"/assets/setup"}
									>
										<Save />
										<span> Agency File Manager</span>
									</a>
								</SidebarMenuButton> */}
							</SidebarGroupContent>
						}
					</SidebarGroup>
				}
			</SidebarContent>
			<SidebarFooter>
				<Button onClick={triggerSupportOpen} variant="default">
					<HelpCircle />
					<span>Need Assistance?</span>
				</Button>
			</SidebarFooter>
		</Sidebar>
	);
};
